// ── Intensity trend math: wind AND central pressure, update-to-update + 24 h ──
// One implementation for every time-indexed intensity series in the app: the
// live best track (6-hourly fixes), NHC's public-advisory ledger (3-hourly
// with intermediates), logged guidance cycles, and hourly replay frames.
//
// Pressure is the forecaster's number: it integrates the whole vortex, falls
// before the wind responds, and is what hurricane-hunter dropsondes measure
// directly. Winds are knots, pressure is mb (hPa); a NEGATIVE Δp is deepening.

export interface TrendPoint {
  t: number; // ms since epoch (UTC)
  vmax: number | null; // kt
  pmin: number | null; // mb — null when missing
}

export interface Change {
  dv: number | null; // kt
  dp: number | null; // mb (negative = deepening)
  hours: number; // actual span between the two points
}

/** ISO string -> ms; naive timestamps from the backend are UTC. */
export function utcMs(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
  return Number.isFinite(ms) ? ms : null;
}

/** Build trend points, dropping missing/placeholder pressures (0 = missing). */
export function toTrendPoints<T>(
  rows: T[],
  get: (r: T) => { time: string | null | undefined; vmax: number | null | undefined; pmin: number | null | undefined },
): TrendPoint[] {
  const out: TrendPoint[] = [];
  for (const r of rows) {
    const g = get(r);
    const t = utcMs(g.time);
    if (t == null) continue;
    out.push({
      t,
      vmax: g.vmax != null && g.vmax > 0 ? g.vmax : null,
      pmin: g.pmin != null && g.pmin > 800 && g.pmin < 1050 ? g.pmin : null,
    });
  }
  return out.sort((a, b) => a.t - b.t);
}

function diff(cur: TrendPoint, prev: TrendPoint): Change {
  return {
    dv: cur.vmax != null && prev.vmax != null ? cur.vmax - prev.vmax : null,
    dp: cur.pmin != null && prev.pmin != null ? cur.pmin - prev.pmin : null,
    hours: (cur.t - prev.t) / 3_600_000,
  };
}

/** Change since the previous point (one NHC update back), whatever its spacing. */
export function changeSincePrev(pts: TrendPoint[], idx = pts.length - 1): Change | null {
  if (idx <= 0 || idx >= pts.length) return null;
  return diff(pts[idx], pts[idx - 1]);
}

/**
 * Change vs the point nearest EXACTLY `hours` earlier, within ±`tolH`.
 * (Taking "the first point at least 18 h back" silently turns a 24 h change
 * into an 18 h one on 6-hourly data.)
 */
export function changeOver(pts: TrendPoint[], hours: number, idx = pts.length - 1, tolH = 3): Change | null {
  if (idx <= 0 || idx >= pts.length) return null;
  const target = pts[idx].t - hours * 3_600_000;
  let best: TrendPoint | null = null;
  let bestErr = Infinity;
  for (let j = idx - 1; j >= 0; j--) {
    const err = Math.abs(pts[j].t - target) / 3_600_000;
    if (err < bestErr) { bestErr = err; best = pts[j]; }
    if (pts[j].t < target - tolH * 3_600_000) break;
  }
  return best && bestErr <= tolH ? diff(pts[idx], best) : null;
}

// ── named rapid-change signals ──
// RI  (+30 kt/24 h): Kaplan & DeMaria (2003) — NHC's operational definition.
// RW  (−30 kt/24 h): the mirror threshold used in the rapid-weakening literature.
// RD  (−42 mb/24 h): "rapid deepening", Holliday & Thompson (1979), 1.75 mb/h.
export interface TrendFlag {
  code: "RI" | "RW" | "RD";
  label: string;
  detail: string;
  color: string;
}

export const RI_KT = 30;
export const RW_KT = -30;
export const RD_MB = -42;

export function trendFlags(d24: Change | null): TrendFlag[] {
  if (!d24) return [];
  const flags: TrendFlag[] = [];
  if (d24.dv != null && d24.dv >= RI_KT) {
    flags.push({
      code: "RI", color: "#EF4444",
      label: d24.dv >= 50 ? "EXTREME RAPID INTENSIFICATION" : "RAPIDLY INTENSIFYING",
      detail: `${signed(d24.dv)} kt in 24 h (RI threshold +30 kt)`,
    });
  }
  if (d24.dp != null && d24.dp <= RD_MB) {
    flags.push({
      code: "RD", color: "#F472B6",
      label: "RAPID DEEPENING",
      detail: `${signed(d24.dp)} mb in 24 h (threshold −42 mb)`,
    });
  }
  if (d24.dv != null && d24.dv <= RW_KT) {
    flags.push({
      code: "RW", color: "#22C55E",
      label: "RAPIDLY WEAKENING",
      detail: `${signed(d24.dv)} kt in 24 h (threshold −30 kt)`,
    });
  }
  return flags;
}

/** "+30" / "−42" (true minus sign) / "0" */
export function signed(v: number): string {
  return v > 0 ? `+${v}` : v < 0 ? `−${Math.abs(v)}` : "0";
}

// ── display helpers ──
// Intensifying (wind up / pressure down) reads hot; weakening reads green —
// the same convention the wind chips already use.
export function windDeltaColor(dv: number): string {
  return dv > 0 ? "#FB7185" : "#22DD66";
}
export function pressureDeltaColor(dp: number): string {
  return dp < 0 ? "#FB7185" : "#22DD66";
}
/** "▲15" / "▼6" / "±0" */
export function arrow(v: number): string {
  if (v === 0) return "±0";
  return `${v > 0 ? "▲" : "▼"}${Math.abs(v)}`;
}
/** span label for an update-to-update change: "6 h", "3 h", "45 min" */
export function spanLabel(hours: number): string {
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  return `${Math.round(hours * 10) / 10} h`.replace(".0 h", " h");
}
